/**
 * OP odds display utilities — pure formatting/alignment helpers for the
 * OddsPapi comparison surface (HYDRA_API.md §"OddsPapi odds").
 *
 * D4-amended (2026-08-03): the OP surface displays DE-VIGGED fair values —
 * `[consensus] / pinnacle` — computed BY HYDRA (contract rule 6c: g1 never
 * computes fair values; everything here renders what the frame carries).
 * PRIMARY = `OpConsensus` fair % at tenths precision (same semantics as
 * the U column, fair-vs-fair); SECONDARY = the anchor book's hydra-computed
 * single-book de-vig (`*FairTenths`); ANCHOR-ONLY when consensus is absent
 * but the anchor fair value is present; neither ⇒ `—`. Freshness is per
 * displayed value (rule 7 as amended): consensus from
 * `consensus.lastUpdateMs`, the anchor secondary/anchor-only from that
 * quote's own `updatedAtMs`, independently — `OpGameOdds.lastUpdateMs` is
 * BARRED for either. The vigged-price helpers below (implied %, rule-6b
 * American preference) are retained for raw-price surfaces. Display-only:
 * nothing here feeds order placement, rank tones, fair dots, or divergence
 * signals.
 */

import { centsToAmericanString } from './pricing/american';
import type {
  HydraOpBookQuote,
  HydraOpConsensus,
  HydraOpMarket,
  HydraOpWithdrawnBook,
} from '../clients/HydraClient';

/** OP quiet-value threshold (5 min) — two jobs, one constant: the OP
 *  velocity arrow's grey-rest lifetime, and the age past which the hover
 *  gains its factual "no delta received in Xm" annotation. NO opacity
 *  dimming (Dan 2026-08-04): a quiet pregame quote isn't wrong, matching
 *  the U column's no-dim precedent. */
export const OP_STALE_MS = 300_000;

/** Pinnacle update-glow duration (globals.css op-anchor-glow): a quote
 *  update lights a soft halo around the number that burns out over 6s —
 *  the SAME window as pinnacle's elevated say in the consensus math
 *  (hydra's extracted Unabated algorithm: sharp arrival weight 20×
 *  decaying to its resting 4× floor over 6s — contract rule 6c). The
 *  halo means "pinnacle's fresh quote is currently ranked high in the
 *  consensus"; when it fades, pinnacle is back to its baseline anchor
 *  weight (Dan 2026-08-04 — keep in sync with hydra's decay constant). */
export const OP_ANCHOR_GLOW_MS = 6_000;

/** Parse a decimal-odds string from the wire (hydra's canonical
 *  number→string serialization — contract rule 6). Valid domain is decimal
 *  odds (1, 10000]; anything else returns null. */
function parseDecimal(decimal: string | null | undefined): number | null {
  if (decimal == null) return null;
  const d = Number(decimal);
  if (!Number.isFinite(d) || d <= 1 || d > 10_000) return null;
  return d;
}

/** Implied win probability (%) of a decimal-odds string, to a tenth:
 *  "1.458" → "68.6". Null on malformed/out-of-domain input. */
export function decimalToImpliedPctText(decimal: string | null | undefined): string | null {
  const d = parseDecimal(decimal);
  if (d == null) return null;
  const pct = 100 / d;
  return (Math.round(pct * 10) / 10).toFixed(1);
}

/** Implied probability as a number (0–100, full precision) — for opacity /
 *  layout math beside the text form above. */
export function decimalToImpliedPct(decimal: string | null | undefined): number | null {
  const d = parseDecimal(decimal);
  return d == null ? null : 100 / d;
}

/** LOCAL decimal→American conversion — the rule-6b FALLBACK only. Integer
 *  magnitudes to match the provider's own integer `priceAmerican` values:
 *  d ≥ 2 → +(d−1)·100; 1 < d < 2 → −100/(d−1). */
export function decimalToAmerican(decimal: string | null | undefined): number | null {
  const d = parseDecimal(decimal);
  if (d == null) return null;
  return d >= 2 ? Math.round((d - 1) * 100) : -Math.round(100 / (d - 1));
}

/** Format an integer American value with its sign: 176 → "+176". */
export function formatOpAmerican(american: number | null | undefined): string {
  if (american == null || !Number.isFinite(american)) return '—';
  return american > 0 ? `+${american}` : `${american}`;
}

/** One side of an OP quote, aligned to a display surface. */
export interface OpSideQuote {
  decimal: string;
  /** Provider-native American value when the frame carried one. */
  american?: number;
  /** Hydra-computed single-book de-vig for this side (rule 6c) — rides
   *  the swap with its quote; never derived locally. */
  fairTenths?: number;
}

/** Rule 6b — one authority for the American subline: PREFER the provider's
 *  own `priceAmerican` (never recompute a present value); convert locally
 *  from the decimal string ONLY when the field is absent. */
export function americanForQuote(side: OpSideQuote | null | undefined): string {
  if (side == null) return '—';
  if (side.american != null && Number.isFinite(side.american)) {
    return formatOpAmerican(side.american);
  }
  return formatOpAmerican(decimalToAmerican(side.decimal));
}

/** A book quote's sides aligned to a row's away/home columns. */
export interface OpAlignedSides {
  /** True when the provider's away landed in the row's home slot. */
  swapped: boolean;
  bookmaker: string;
  updatedAtMs: number;
  away: OpSideQuote;
  home: OpSideQuote;
  /** Present ⇔ the frame carried `drawDecimal` (3-way — rule 5). The draw
   *  has no side to swap. */
  draw: OpSideQuote | null;
}

/**
 * THE single away/home alignment helper for OP quotes (the consensus swap
 * rule has four historical copies — GameTable/ValueDashboard/GameDetail/
 * PositionTracker; OP gets exactly one, here). Contract rule 4: the frame's
 * `awayTeam`/`homeTeam` are the PROVIDER's orientation in canonical codes;
 * when `op.awayTeam === rowHomeCode` the provider's away is the row's home,
 * so the sides swap.
 */
export function alignOpSidesToRow(
  op: { awayTeam: string; homeTeam: string },
  book: HydraOpBookQuote,
  rowHomeCode: string | null | undefined,
): OpAlignedSides {
  const swapped = rowHomeCode != null && op.awayTeam === rowHomeCode;
  const providerAway: OpSideQuote = {
    decimal: book.awayDecimal,
    american: book.awayAmerican,
    fairTenths: book.awayFairTenths,
  };
  const providerHome: OpSideQuote = {
    decimal: book.homeDecimal,
    american: book.homeAmerican,
    fairTenths: book.homeFairTenths,
  };
  return {
    swapped,
    bookmaker: book.bookmaker,
    updatedAtMs: book.updatedAtMs,
    away: swapped ? providerHome : providerAway,
    home: swapped ? providerAway : providerHome,
    draw: book.drawDecimal != null
      ? { decimal: book.drawDecimal, american: book.drawAmerican, fairTenths: book.drawFairTenths }
      : null,
  };
}

// ── D4-amended fair-value display (rule 6c — hydra-computed only) ─────

/** Fair probability text from hydra tenths (0–1000): 542 → "54.2".
 *  RENDERS the wire value — never computes or adjusts it (rule 6c).
 *  Null on absent/out-of-domain input (⇒ that value doesn't render). */
export function fairPctText(tenths: number | null | undefined): string | null {
  if (tenths == null || !Number.isFinite(tenths) || tenths < 0 || tenths > 1000) return null;
  return (tenths / 10).toFixed(1);
}

/** American subline for a fair value — EXACTLY the U column's derivation
 *  (GameTable pctAmerican): display-cents = tenths/10 when tenths are
 *  present, else integer cents; open-interval (0,100) guard; canonical
 *  centsToAmericanString; its "—" boundary sentinel maps to null (the
 *  cell suppresses the subline). */
export function fairAmericanText(
  tenths: number | null | undefined,
  cents: number | null | undefined,
): string | null {
  const displayCents = tenths != null && Number.isFinite(tenths) ? tenths / 10 : cents;
  if (displayCents == null || !Number.isFinite(displayCents)) return null;
  if (displayCents <= 0 || displayCents >= 100) return null;
  const s = centsToAmericanString(displayCents);
  return s === '—' ? null : s;
}

/** The displayed anchor book — the quote whose hydra-computed `*FairTenths`
 *  power the PNCL number. STRICTLY pinnacle (G1 strikethrough round 1):
 *  the as-built serializer computes fair tenths for EVERY viable
 *  surviving book, so ANY fallback — even single-carrier — can promote a
 *  sparse field state (a lone DraftKings/FanDuel quote) into the PNCL
 *  slot, poison the last-known-anchor cache, and later strike a field
 *  value as "Pinnacle pulled". The column is named PNCL; nothing else
 *  ever fills it. */
export function selectOpAnchorBook(market: HydraOpMarket): HydraOpBookQuote | null {
  return (
    market.books.find(
      (b) => b.bookmaker === 'pinnacle' && b.awayFairTenths != null && b.homeFairTenths != null,
    ) ?? null
  );
}

/** The OP/PNCL column-header LABEL — one constant across the lockstep
 *  theads (Dan 2026-08-03: pinnacle shown with equal importance). */
export const OP_HEADER_LABEL = 'OP/PNCL';

/** The OP column-header tooltip — ONE string shared by every lockstep
 *  thead (GameTable / MultiOutcomeTable / MoneylineSection /
 *  GameLinesSection) so the surfaces can't drift. Names the provider,
 *  book count, and anchor, and explains the consensus construction:
 *  pinnacle-anchored, weighted with the other entitled sportsbooks
 *  (Dan 2026-08-03). */
export function opHeaderTooltip(
  anchorBookmaker: string | null | undefined,
  contributingBooks: number | null | undefined,
): string {
  const anchor = anchorBookmaker ?? 'pinnacle';
  return `OddsPapi de-vigged fair values: OP consensus / ${anchor}. The consensus is ${anchor} at elevated weight blended with the other entitled sportsbooks${
    contributingBooks != null ? ` (${contributingBooks} books contributing)` : ''
  }; the PNCL number is ${anchor}'s own de-vigged price. Hydra-computed, NOT a book price. Comparison only.`;
}

/** Inline `animation-delay` for the anchor update glow (globals.css
 *  op-anchor-glow): NEGATIVE age at mount so a mid-aged quote starts
 *  mid-burn-out, clamped at the glow end (OP_ANCHOR_GLOW_MS) so anything
 *  older mounts with no halo via forwards fill. */
export function opAnchorGlowDelayMs(updatedAtMs: number, nowMs: number = Date.now()): number {
  return -Math.min(Math.max(0, nowMs - updatedAtMs), OP_ANCHOR_GLOW_MS);
}


// ── Phase 3 (rule 12): pinnacle withdrawal — the strikethrough ───────

/** Display labels for the provider's withdrawal reasons. UNKNOWN reason
 *  strings render as generic 'withdrawn' (additive tolerance — the union
 *  is open on the wire). */
const OP_WITHDRAW_REASON_LABELS: Record<string, string> = {
  suspended: 'suspended',
  stale_link: 'link down',
  no_odds: 'not offered',
  market_inactive: 'market inactive',
};

export function opWithdrawReasonLabel(reason: string): string {
  return OP_WITHDRAW_REASON_LABELS[reason] ?? 'withdrawn';
}

/** The pinnacle entry of a market's withdrawnBooks, or null. Books other
 *  than pinnacle are ignored for display — field books aren't
 *  individually rendered. */
export function opPinnacleWithdrawal(market: HydraOpMarket): HydraOpWithdrawnBook | null {
  return market.withdrawnBooks?.find((w) => w.bookmaker === 'pinnacle') ?? null;
}

/** Strikethrough tooltip: reason + age, age from `sinceMs` ONLY (rule-7
 *  discipline — serverTs and op.lastUpdateMs barred). */
export function opWithdrawalTitle(w: HydraOpWithdrawnBook, nowMs: number = Date.now()): string {
  return `Pinnacle pulled — ${opWithdrawReasonLabel(w.reason)}, ${formatOpAge(w.sinceMs, nowMs)}`;
}

/** The NEXT display boundary of the withdrawal age string (round-1
 *  finding 2: the tooltip is computed at render, so without a clock
 *  authority a quiet mounted row says "4m ago" forever). formatOpAge
 *  ROUNDS to minutes, so the rendered value flips at half-minute
 *  crossings of `sinceMs`; feed this to useDeadlineTick and re-arm per
 *  fire — one bounded timeout per displayed withdrawal, never a ticker. */
export function opWithdrawalAgeDeadline(sinceMs: number, nowMs: number = Date.now()): number {
  const ageMs = Math.max(0, nowMs - sinceMs);
  const roundedMins = Math.round(ageMs / 60_000);
  return sinceMs + roundedMins * 60_000 + 30_000;
}

/** The anchor book's last-known fair values (provider orientation),
 *  cached store-side per bucket — the number the strikethrough is drawn
 *  over. This caches a VALUE for display under an EXPLICIT wire
 *  withdrawal; it never infers the withdrawal itself (rule 12 forbids
 *  inference on absence — the badge exists iff the frame lists it). */
export interface OpLastAnchorFair {
  bookmaker: string;
  awayFairTenths: number;
  homeFairTenths: number;
  drawFairTenths?: number;
}

/** One side of the fair-value view, aligned to a display surface. */
export interface OpFairSideView {
  /** Consensus fair tenths (0–1000) — the PRIMARY display value. */
  consensusTenths: number | null;
  /** Consensus integer cents — the American subline's fallback input. */
  consensusCents: number | null;
  /** Anchor book single-de-vig tenths — the SECONDARY display value,
   *  and the primary in anchor-only mode. */
  anchorFairTenths: number | null;
  /** Last-known anchor fair tenths for this side (store cache) — the
   *  value the strikethrough is drawn over when the anchor is withdrawn.
   *  Null when never seen this session. */
  withdrawnLastTenths: number | null;
}

/**
 * The `[consensus] / pinnacle` display model (architecture §8, D4-amended)
 * — ONE computation shared by the dashboard cell and the panel O row.
 * Renders hydra's values verbatim (rule 6c) aligned to the row's columns
 * (rule 4, via the same swap predicate as `alignOpSidesToRow`).
 *
 * `mode: 'consensus'` — consensus present (anchor secondary shown when its
 * fair fields are present too); `mode: 'anchor-only'` — consensus absent
 * but the anchor fair value present (visually marked by callers). Returns
 * NULL when neither exists — the cell renders `—`, the panel row hides.
 *
 * Freshness (rule 7 as amended) is PER DISPLAYED VALUE and independent:
 * the consensus primary from `consensusLastUpdateMs`, the anchor
 * secondary/anchor-only from `anchorUpdatedAtMs`. `OpGameOdds.lastUpdateMs`
 * is deliberately absent from this view — it is BARRED as a freshness
 * source for displayed values.
 */
export interface OpFairView {
  mode: 'consensus' | 'anchor-only';
  /** True when the provider's away landed in the row's home slot. */
  swapped: boolean;
  /** Anchor book slug (e.g. 'pinnacle') — null when no anchor fair. */
  anchorBookmaker: string | null;
  contributingBooks: number | null;
  pinnacleAnchored: boolean | null;
  /** Consensus freshness authority (`consensus.lastUpdateMs`). */
  consensusLastUpdateMs: number | null;
  /** Anchor freshness authority (that quote's own `updatedAtMs`). */
  anchorUpdatedAtMs: number | null;
  /** Rule 12: pinnacle's current withdrawal entry, verbatim from the
   *  frame — null when pinnacle is quoting or unlisted. Age from its
   *  `sinceMs` ONLY. Cleared the instant a frame arrives without it
   *  (frames are absolute; no latching). */
  pinnacleWithdrawn: HydraOpWithdrawnBook | null;
  away: OpFairSideView;
  home: OpFairSideView;
  /** Present ⇔ the frame carries a draw fair value (3-way). No swap. */
  draw: OpFairSideView | null;
}

/** Integer-line push caveat (hydracore §"Spread & total markets", inherited
 *  OG model gap): a whole-number line can PUSH, and the 2-outcome fair
 *  model has no explicit push mass — fair values on such lines are the
 *  de-vig of the book's two-way prices as quoted. Consumers must not
 *  present them as no-push win probabilities; this note rides the hover. */
export const OP_INTEGER_LINE_NOTE =
  'Whole-number line — can push. Fair values are the two-way de-vig of quoted prices, not win probabilities conditioned on no push.';

export function opLineCanPush(points: number | null | undefined): boolean {
  return points != null && Number.isInteger(points);
}

export function buildOpFairView(
  op: { awayTeam: string; homeTeam: string; market: HydraOpMarket },
  rowHomeCode: string | null | undefined,
  lastAnchor: OpLastAnchorFair | null = null,
): OpFairView | null {
  const consensus: HydraOpConsensus | undefined = op.market.consensus;
  const anchor = selectOpAnchorBook(op.market);
  // Rule 12: the withdrawal comes from the FRAME only — a view exists
  // for it even when no fair value renders (struck placeholder).
  const pinnacleWithdrawn = opPinnacleWithdrawal(op.market);
  if (consensus == null && anchor == null && pinnacleWithdrawn == null) return null;
  // The struck value: last-known anchor fair, shown ONLY under an
  // explicit withdrawal (never as a live value substitute) and ONLY when
  // the cached identity is actually pinnacle (round-1 defensive guard —
  // the strict selector should make any other identity impossible, but
  // provenance on a "Pinnacle pulled" strike is never assumed).
  const struck =
    pinnacleWithdrawn != null && lastAnchor?.bookmaker === 'pinnacle' ? lastAnchor : null;

  const swapped = rowHomeCode != null && op.awayTeam === rowHomeCode;
  const providerAway: OpFairSideView = {
    consensusTenths: consensus?.awayFairProbTenths ?? null,
    consensusCents: consensus?.awayFairProbCents ?? null,
    anchorFairTenths: anchor?.awayFairTenths ?? null,
    withdrawnLastTenths: struck?.awayFairTenths ?? null,
  };
  const providerHome: OpFairSideView = {
    consensusTenths: consensus?.homeFairProbTenths ?? null,
    consensusCents: consensus?.homeFairProbCents ?? null,
    anchorFairTenths: anchor?.homeFairTenths ?? null,
    withdrawnLastTenths: struck?.homeFairTenths ?? null,
  };
  const draw: OpFairSideView | null =
    consensus?.drawFairProbTenths != null ||
    anchor?.drawFairTenths != null ||
    struck?.drawFairTenths != null
      ? {
          consensusTenths: consensus?.drawFairProbTenths ?? null,
          consensusCents: consensus?.drawFairProbCents ?? null,
          anchorFairTenths: anchor?.drawFairTenths ?? null,
          withdrawnLastTenths: struck?.drawFairTenths ?? null,
        }
      : null;
  return {
    mode: consensus != null ? 'consensus' : 'anchor-only',
    swapped,
    anchorBookmaker: anchor?.bookmaker ?? null,
    contributingBooks: consensus?.contributingBooks ?? null,
    pinnacleAnchored: consensus?.pinnacleAnchored ?? null,
    consensusLastUpdateMs: consensus?.lastUpdateMs ?? null,
    anchorUpdatedAtMs: anchor?.updatedAtMs ?? null,
    pinnacleWithdrawn,
    away: swapped ? providerHome : providerAway,
    home: swapped ? providerAway : providerHome,
    draw,
  };
}

/** Per-value quiet check (contract rule 7 — from the value's OWN
 *  timestamp, NEVER the frame's `serverTs` or `OpGameOdds.lastUpdateMs`).
 *  Gates the hover's "no delta received in Xm" annotation PER DISPLAYED
 *  VALUE — consensus with `consensus.lastUpdateMs`, the anchor with that
 *  quote's `updatedAtMs`, independently. Hover-annotation only; there is
 *  no opacity dim (Dan 2026-08-04). */
export function opIsStale(updatedAtMs: number, nowMs: number = Date.now()): boolean {
  return nowMs - updatedAtMs > OP_STALE_MS;
}

/** Human duration since a timestamp: "7m" / "2h". */
export function formatOpDuration(sinceMs: number, nowMs: number = Date.now()): string {
  const ageMs = Math.max(0, nowMs - sinceMs);
  const mins = Math.round(ageMs / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.round(mins / 60)}h`;
}

/** Human age for tooltips: "7m ago" / "2h ago". */
export function formatOpAge(updatedAtMs: number, nowMs: number = Date.now()): string {
  return `${formatOpDuration(updatedAtMs, nowMs)} ago`;
}

/** The quiet-value tooltip line (Dan 2026-08-04): an aged quote is not
 *  WRONG — pregame books can simply sit — so the hover reports the fact
 *  ("no delta received in 12m"), never a "STALE" verdict. The visual
 *  dimming stays; only the wording is factual. */
export function opNoDeltaText(label: string, sinceMs: number, nowMs: number = Date.now()): string {
  return `${label}: no delta received in ${formatOpDuration(sinceMs, nowMs)}.`;
}

/** Full-feed B (hydracore §"Full-feed B", 2026-08-17): g1 Period vocab →
 *  the vendor `period` string the wire carries VERBATIM on non-full-game
 *  entries. The JOIN key for period-scoped OP lookups ('full' has no
 *  entry — full-game buckets key on periodTypeId 1). MLB's F5 rides the
 *  innings union; halves/quarters ride pN unions. Unknown vendor shapes
 *  simply never match a g1 period — they stay reachable through
 *  getOpMarketsFor, never guessed at. */
export const OP_PERIOD_WIRE: Record<string, string> = {
  F5: 'p1+p2+p3+p4+p5',
  '1H': 'p1+p2',
  '2H': 'p3+p4',
  '1Q': 'p1',
  '2Q': 'p2',
  '3Q': 'p3',
  '4Q': 'p4',
};
